import { AlertCircle, CheckCircle } from "@untitledui/icons";
import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";
import { cx } from "@/utils/cx";

// Untitled UI's free set has no notification component. This is the smallest one that does the
// job: what just happened, bottom-right, gone in a few seconds — announced to screen readers.

interface Toast {
  id: number;
  tone: "success" | "error";
  text: string;
}
interface Toaster {
  success(text: string): void;
  error(err: unknown): void;
}

const ToastContext = createContext<Toaster>({ success: () => undefined, error: () => undefined });
export const useToast = () => useContext(ToastContext);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const next = useRef(0);
  const push = useCallback((tone: Toast["tone"], text: string) => {
    const id = ++next.current;
    setToasts((all) => [...all, { id, tone, text }]);
    setTimeout(() => setToasts((all) => all.filter((t) => t.id !== id)), tone === "error" ? 8000 : 4000);
  }, []);
  const toaster = useMemo<Toaster>(() => ({ success: (text) => push("success", text), error: (err) => push("error", err instanceof Error ? err.message : String(err)) }), [push]);

  return (
    <ToastContext.Provider value={toaster}>
      {children}
      <div aria-live="polite" className="pointer-events-none fixed right-4 bottom-4 z-50 flex w-full max-w-sm flex-col gap-2">
        {toasts.map((t) => (
          <div key={t.id} role={t.tone === "error" ? "alert" : "status"} className="pointer-events-auto flex items-start gap-3 rounded-xl bg-primary p-4 shadow-lg ring-1 ring-secondary duration-200 animate-in fade-in slide-in-from-bottom-2">
            {t.tone === "error" ? <AlertCircle className="size-5 shrink-0 text-fg-error-primary" aria-hidden="true" /> : <CheckCircle className="size-5 shrink-0 text-fg-success-primary" aria-hidden="true" />}
            <p className={cx("text-sm font-medium", t.tone === "error" ? "text-error-primary" : "text-primary")}>{t.text}</p>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
